import styled from '@emotion/styled';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import { getDay, getMonthString } from '@utils/dates';
import pushRoute from '@utils/pushRoute';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { DOUBT_CARD_STATE } from './DoubtSolution/constants';

export const CardWrapper = styled.div`
  width: 272px;
  height: 220px;
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  padding: ${Spacings.SPACING_4B};
  background-color: var(--color-base-1);
  grid-column: span 6;
  cursor: pointer;
  margin-bottom: ${({ marginBottom }) => marginBottom || Spacings.SPACING_9B};
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_2B} auto;
  }
`;
const BackgroundWrapper = styled.div`
  width: 240px;
  height: 136px;
  background-image: url('${(props) => props.bgImageSrc}');
  display: block;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: 1;
  filter: blur(64px);
  top: 0;
  left: 0;
`;

const ImageWrapper = styled.div`
  width: 240px;
  height: 136px;
  position: relative;
  display: block;
  overflow: hidden;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
`;

const DountImage = styled.img`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CardSubject = styled(H6)`
  margin-top: ${Spacings.SPACING_4B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const CardInfoWrapper = styled.div`
  margin-top: ${Spacings.SPACING_1B};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardInfo = styled(P2)`
  color: var(--color-text-secondary);
`;

const DoubtTag = styled.div`
  height: 18px;
  width: ${Spacings.SPACING_24B};
  padding: ${Spacings.SPACING_3} ${Spacings.SPACING_1B};
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Spacings.SPACING_1B};
`;

const TagText = styled.span`
  z-index: 2;
  font-size: ${Spacings.SPACING_10};
  color: ${({ isNew }) =>
    isNew ? 'var(--color-i-white)' : 'var(--color-text-secondary)'};
`;

const Bgdiv = styled.div`
  height: 18px;
  width: ${Spacings.SPACING_24B};
  position: absolute;
  background-color: var(--color-i-blue);
  border-radius: ${Spacings.SPACING_1B};
`;

export const DoubtImageWrapper = ({ content }) => {
  return (
    <ImageWrapper>
      <BackgroundWrapper bgImageSrc={content} />
      <DountImage src={content} alt="doubt-image" />
    </ImageWrapper>
  );
};

export const getCardInfo = (timeStamp, tag) => {
  const date = `${getDay(timeStamp)} ${getMonthString(timeStamp)}`;
  let formattedTag;
  if (tag) {
    formattedTag = `${tag[0].toUpperCase()}${tag
      .slice(1, tag.length)
      .toLowerCase()}`;
    return `${date} • ${formattedTag}`;
  }
  return date;
};

const DoubtCard = ({ data, marginBottom, goalUID }) => {
  const { attachments = [], state, tag, timeStamp, subject, uid } = data || {};
  const { goalInfo } = useGoalInfo(goalUID);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const handleCardClick = () => {
    pushRoute({
      pathname: `/goal/${goalInfo.slug}/${goalInfo.uid}/doubts-and-solutions/doubt/${uid}`
    });
  };

  const isNew = state === DOUBT_CARD_STATE.newSoluton;
  const imageContent = attachments.find(
    (content) =>
      content.content_type === 'IMAGE' || content.contentType === 'IMAGE'
  );
  return (
    <CardWrapper onClick={handleCardClick} marginBottom={marginBottom}>
      <DoubtImageWrapper content={imageContent?.content} />
      <CardSubject>{subject?.name}</CardSubject>
      <CardInfoWrapper>
        <CardInfo>{getCardInfo(timeStamp, tag)}</CardInfo>
        {state && (
          <DoubtTag isDarkModeOn={isDarkModeOn} type="success">
            {isNew ? <Bgdiv /> : null}
            {state ? <TagText isNew={isNew}>{state}</TagText> : null}
          </DoubtTag>
        )}
      </CardInfoWrapper>
    </CardWrapper>
  );
};

export default DoubtCard;
